/*
  sc-blog.js
  Seed Cube blog template — UI interactions.
  Features:
    1. TOC active-section tracking (always exactly one active item)
    2. Smooth in-page anchor scrolling that respects the sticky header
    3. Sticky buy bar
  The sidebar show/hide is handled entirely by CSS position: sticky — no JS.
  Vanilla JS only. Runs after DOMContentLoaded.
*/

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getHeaderOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-h');
    var value = parseInt(raw, 10);
    return (isNaN(value) ? 88 : value) + 24;
  }

  // ── TOC active-section tracking ───────────────────────────────────────────
  function initTOCTracking() {
    var links = Array.prototype.slice.call(document.querySelectorAll('[data-sc-toc-link]'));
    if (!links.length) return;

    var targets = links
      .map(function (link) {
        var id = (link.getAttribute('href') || '').replace('#', '');
        var el = id ? document.getElementById(id) : null;
        return el ? { link: link, el: el } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    var activeLink = null;
    function setActive(link) {
      if (link === activeLink) return;
      if (activeLink) activeLink.classList.remove('sc-blog-toc__link--active');
      if (link) link.classList.add('sc-blog-toc__link--active');
      activeLink = link;
    }

    function update() {
      var offset = getHeaderOffset() + 8;
      var current = targets[0];
      for (var i = 0; i < targets.length; i++) {
        // The active heading is the last one whose top has scrolled past the offset.
        if (targets[i].el.getBoundingClientRect().top - offset <= 0) {
          current = targets[i];
        } else {
          break;
        }
      }
      setActive(current.link);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    // ── Smooth anchor scrolling that accounts for the sticky header ─────────
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var id = (link.getAttribute('href') || '').replace('#', '');
        var el = id ? document.getElementById(id) : null;
        if (!el) return;
        event.preventDefault();
        var top = el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
        window.scrollTo({
          top: top,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        // Close the mobile accordion after a jump.
        var toggle = document.querySelector('.sc-blog-toc__checkbox');
        if (toggle && window.innerWidth < 1024) toggle.checked = false;
      });
    });
  }

  // ── Key facts: scroll reveal + count-up ───────────────────────────────────
  function animateCount(el, duration) {
    var raw = el.textContent.trim();
    // Match a leading number (supports decimals and thousands separators).
    var match = raw.match(/-?\d[\d,]*\.?\d*/);
    if (!match) return; // Nothing numeric — leave the text untouched.

    var numberStr = match[0];
    var target = parseFloat(numberStr.replace(/,/g, ''));
    if (isNaN(target)) return;

    var decimals = (numberStr.split('.')[1] || '').length;
    var prefix = raw.slice(0, match.index);
    var suffix = raw.slice(match.index + numberStr.length);
    var start = null;

    function format(value) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }

    function step(timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      // easeOutCubic
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = prefix + format(target * eased) + suffix;
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.textContent = raw;
      }
    }

    el.textContent = prefix + format(0) + suffix;
    window.requestAnimationFrame(step);
  }

  function initKeyFacts() {
    var sections = Array.prototype.slice.call(document.querySelectorAll('[data-sc-keyfacts]'));
    if (!sections.length) return;

    sections.forEach(function (section) {
      var cards = Array.prototype.slice.call(section.querySelectorAll('.sc-blog-keyfacts__card'));
      if (!cards.length) return;

      var countUp = section.hasAttribute('data-sc-keyfacts-countup') && !prefersReducedMotion;

      if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        return; // Leave stats in their static, fully-visible state.
      }

      section.classList.add('sc-blog-keyfacts--ready');
      cards.forEach(function (card, index) {
        card.style.setProperty('--sc-reveal-delay', index * 110 + 'ms');
      });

      var observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          cards.forEach(function (card, index) {
            card.classList.add('is-visible');
            if (countUp) {
              var value = card.querySelector('[data-sc-value]');
              if (value) {
                window.setTimeout(function () {
                  animateCount(value, 1400);
                }, index * 110);
              }
            }
          });
          obs.unobserve(entry.target);
        });
      }, { threshold: 0.35 });

      observer.observe(section);
    });
  }

  // ── Sticky buy bar ──────────────────────────────────────────────────────
  function initStickyBuyBar() {
    var bar = document.getElementById('sc-blog-buy-bar');
    var trigger = document.getElementById('sc-blog-product-trigger');
    if (!bar || !trigger) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('sc-blog-buy-bar--visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });

    observer.observe(trigger);
  }

  // ── Init ────────────────────────────────────────────────────────────────
  function init() {
    initTOCTracking();
    initStickyBuyBar();
    initKeyFacts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
